import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { ChainOfThought, ChainOfThoughtConnector, ChainOfThoughtContent, ChainOfThoughtHeader, ChainOfThoughtStep, ChainOfThoughtStepToggle, MessageResponse } from '@newfront-insurance/nova-core';
import { CheckCircle2Icon } from 'lucide-react';
import type { ReactNode } from 'react';
import { memo, useCallback, useLayoutEffect, useRef } from 'react';
import { CoTToolInput, CoTToolOutput, CoTToolSearchResults } from './chain-of-thought-renderers';
import { type CoTStep, getCoTStepIcon, getCoTStepKey, getCoTStepLabel, getCoTStepStatus, stripLeadingTitle } from '../lib/chain-of-thought-utils';
interface ChainOfThoughtBlockProps {
  steps: CoTStep[];
  reasoningIsStreaming: boolean;
  stepProgressMap: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Shared step renderer
// ---------------------------------------------------------------------------

function renderStep(step: CoTStep, stepIndex: number, stepProgressMap: Record<string, string>, isLast: boolean): ReactNode {
  const stepKey = getCoTStepKey(step, stepIndex);
  const stepStatus = getCoTStepStatus(step);
  const stepProgress = step.type === 'tool' ? stepProgressMap[stepKey] : undefined;
  const inputDescription = step.type === 'tool' && step.input != null ? <CoTToolInput toolName={step.toolName} input={step.input} /> : undefined;
  const description = inputDescription && stepProgress ? <>
        {inputDescription}
        <div>{stepProgress}</div>
      </> : inputDescription ?? stepProgress;
  return <ChainOfThoughtStep key={stepKey} icon={getCoTStepIcon(step)} label={getCoTStepLabel(step)} description={description} status={stepStatus} isLast={isLast}>
      {step.type === 'reasoning' && step.text && <MessageResponse className="text-muted-foreground">
          {step.title ? stripLeadingTitle(step.text) : step.text}
        </MessageResponse>}
      {step.type === 'tool' && stepStatus === 'complete' && step.output != null && <>
          <CoTToolSearchResults toolName={step.toolName} output={step.output} />
          <div className="text-muted-foreground size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
            <CoTToolOutput toolName={step.toolName} output={step.output} />
          </div>
        </>}
    </ChainOfThoughtStep>;
}

// ---------------------------------------------------------------------------
// useStableHeight — prevents container from shrinking during streaming
// ---------------------------------------------------------------------------

function useStableHeight(isStreaming) {
  const $ = _c(2);
  const ref = useRef(null);
  const maxHeightRef = useRef(0);
  let t0;
  if ($[0] !== isStreaming) {
    t0 = () => {
      const el = ref.current;
      if (!el) {
        return;
      }
      if (isStreaming) {
        const height = el.scrollHeight;
        if (height > maxHeightRef.current) {
          maxHeightRef.current = height;
        }
        el.style.minHeight = `${maxHeightRef.current}px`;
      } else {
        el.style.minHeight = "";
        maxHeightRef.current = 0;
      }
    };
    $[0] = isStreaming;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useLayoutEffect(t0);
  return ref;
}

// ---------------------------------------------------------------------------
// ChainOfThoughtBlock
// ---------------------------------------------------------------------------

export const ChainOfThoughtBlock = memo(t0 => {
  const $ = _c(44);
  const {
    steps,
    reasoningIsStreaming,
    stepProgressMap
  } = t0;
  const stableRef = useStableHeight(reasoningIsStreaming);
  const analytics = useAnalytics();
  let t1;
  if ($[0] !== analytics || $[1] !== steps.length) {
    t1 = open => {
      analytics.track({
        event: "Chain of Thought Previous Steps Toggled",
        properties: {
          open,
          stepCount: steps.length
        }
      });
    };
    $[0] = analytics;
    $[1] = steps.length;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handlePreviousStepsToggle = t1;
  let t2;
  if ($[3] !== analytics || $[4] !== steps.length) {
    t2 = open_0 => {
      analytics.track({
        event: "Chain of Thought Toggled",
        properties: {
          open: open_0,
          stepCount: steps.length
        }
      });
    };
    $[3] = analytics;
    $[4] = steps.length;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleHeaderToggle = t2;
  if (reasoningIsStreaming) {
    let T0;
    let hasPreviousSteps;
    let t3;
    let t4;
    let t5;
    let t6;
    if ($[6] !== handlePreviousStepsToggle || $[7] !== stableRef || $[8] !== stepProgressMap || $[9] !== steps) {
      const previousSteps = steps.slice(0, -1);
      hasPreviousSteps = previousSteps.length > 0;
      t6 = stableRef;
      T0 = ChainOfThought;
      t3 = true;
      t4 = handlePreviousStepsToggle;
      t5 = hasPreviousSteps && <><ChainOfThoughtStepToggle count={previousSteps.length} /><ChainOfThoughtContent>{previousSteps.map((step, i) => renderStep(step, i, stepProgressMap, false))}</ChainOfThoughtContent></>;
      $[6] = handlePreviousStepsToggle;
      $[7] = stableRef;
      $[8] = stepProgressMap;
      $[9] = steps;
      $[10] = T0;
      $[11] = hasPreviousSteps;
      $[12] = t3;
      $[13] = t4;
      $[14] = t5;
      $[15] = t6;
    } else {
      T0 = $[10];
      hasPreviousSteps = $[11];
      t3 = $[12];
      t4 = $[13];
      t5 = $[14];
      t6 = $[15];
    }
    let t7;
    if ($[16] !== hasPreviousSteps) {
      t7 = hasPreviousSteps && <ChainOfThoughtConnector />;
      $[16] = hasPreviousSteps;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    const t8 = steps[steps.length - 1];
    const t9 = steps.length - 1;
    let t10;
    if ($[18] !== stepProgressMap || $[19] !== t8 || $[20] !== t9) {
      t10 = renderStep(t8, t9, stepProgressMap, true);
      $[18] = stepProgressMap;
      $[19] = t8;
      $[20] = t9;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    let t11;
    if ($[22] !== T0 || $[23] !== t10 || $[24] !== t3 || $[25] !== t4 || $[26] !== t5 || $[27] !== t7) {
      t11 = <T0 isStreaming={t3} onOpenChange={t4}>{t5}{t7}{t10}</T0>;
      $[22] = T0;
      $[23] = t10;
      $[24] = t3;
      $[25] = t4;
      $[26] = t5;
      $[27] = t7;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    let t12;
    if ($[29] !== t11 || $[30] !== t6) {
      t12 = <div ref={t6}>{t11}</div>;
      $[29] = t11;
      $[30] = t6;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    return t12;
  }
  let t3;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ChainOfThoughtHeader>Chain of Thought</ChainOfThoughtHeader>;
    $[32] = t3;
  } else {
    t3 = $[32];
  }
  let t4;
  if ($[33] !== stepProgressMap || $[34] !== steps) {
    let t5;
    if ($[36] !== stepProgressMap) {
      t5 = (step_0, i_0) => renderStep(step_0, i_0, stepProgressMap, false);
      $[36] = stepProgressMap;
      $[37] = t5;
    } else {
      t5 = $[37];
    }
    t4 = steps.map(t5);
    $[33] = stepProgressMap;
    $[34] = steps;
    $[35] = t4;
  } else {
    t4 = $[35];
  }
  let t5;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ChainOfThoughtStep key="done" icon={CheckCircle2Icon} label="Done" status="complete" isLast={true} />;
    $[38] = t5;
  } else {
    t5 = $[38];
  }
  let t6;
  if ($[39] !== t4) {
    t6 = <ChainOfThoughtContent>{t4}{t5}</ChainOfThoughtContent>;
    $[39] = t4;
    $[40] = t6;
  } else {
    t6 = $[40];
  }
  let t7;
  if ($[41] !== handleHeaderToggle || $[42] !== t6) {
    t7 = <ChainOfThought isStreaming={false} onOpenChange={handleHeaderToggle}>{t3}{t6}</ChainOfThought>;
    $[41] = handleHeaderToggle;
    $[42] = t6;
    $[43] = t7;
  } else {
    t7 = $[43];
  }
  return t7;
});
ChainOfThoughtBlock.displayName = 'ChainOfThoughtBlock';